<template>
  <main class="px-4 sm:px-6 lg:px-8 py-6">
    <h1 class="text-xl font-semibold text-center">
      Extract (handwritten) text from any image with AI.
    </h1>

    <!-- Professional Explanation Section -->
    <div class="max-w-5xl mx-auto mt-8 mb-8 text-gray-700 space-y-6">

      <!-- How It Works Section -->
      <div class="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">How It Works</h2>
        <div class="space-y-4 text-left">
          <p>
            <strong>1. Upload Your Image:</strong> Start by uploading an image containing any text, including complex handwriting, tables, or graphs.
          </p>
          <p>
            <strong>2. AI-Powered Processing:</strong> Unlike traditional OCR, this service uses advanced LLM Vision technology (based on Qwen) to understand context. It intelligently interprets hard-to-read text, just like a human would, filling in gaps based on the surrounding information.
          </p>
          <p>
            <strong>3. Download Your Text:</strong> You can leave this page and return later. Once processing is complete, you can view, copy, or download the extracted text.
          </p>
        </div>
      </div>

      <!-- Important Notes Section -->
      <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <!-- Heroicon name: solid/exclamation -->
            <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.21 3.03-1.742 3.03H4.42c-1.532 0-2.492-1.696-1.742-3.03l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-yellow-700">
              This is a <strong>100% FREE demo service.</strong> All uploads are deleted after 48 hours. Processing a single image can take up to 5 minutes. For business inquiries or custom solutions, please contact <a href="mailto:support@easymarks.ai" class="font-medium underline text-yellow-800 hover:text-yellow-900">support@easymarks.ai</a>.
            </p>
          </div>
        </div>
      </div>

    </div>

    <!-- Uppy Dashboard Container -->
    <div class="max-w-5xl mx-auto">
      <div id="uppy-dashboard"></div>
    </div>

    <div
        v-if="previousUploads.length"
        class="uploads-table-container overflow-x-auto mt-6"
    >
      <h3 class="text-lg font-semibold mb-2">Previous Uploads</h3>

      <!-- Table for medium and larger screens -->
      <div class="hidden md:block">
        <table class="uploads-table min-w-full bg-white rounded-lg overflow-hidden shadow table-auto">
          <thead class="bg-gray-100">
          <tr>
            <th class="px-2 py-2 text-left text-sm font-medium text-gray-700">
              Filename
            </th>
            <th class="px-2 py-2 text-left text-sm font-medium text-gray-700">
              Status
            </th>
            <th class="px-2 py-2 text-left text-sm font-medium text-gray-700">
              Actions
            </th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="upload in previousUploads"
              :key="upload.id"
              class="hover:bg-gray-50"
          >
            <td class="px-2 py-2 text-sm text-gray-800 truncate">
              {{ upload.filename }}
            </td>
            <td class="px-2 py-2 text-sm text-gray-800">
              <div v-if="upload.status === 'PROCESSING'" class="flex items-center">
                <svg
                    class="animate-spin h-4 w-4 text-blue-600 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                  ></circle>
                  <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Processing
              </div>
              <div v-else-if="upload.status === 'PROCESSED'" class="flex items-center">
                <svg
                    class="h-4 w-4 text-green-600 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12v6m0 0l-3-3m3 3l3-3"
                  />
                </svg>
                Completed
              </div>
              <div v-else-if="upload.status === 'FAILED'" class="flex items-center">
                <svg
                    class="h-4 w-4 text-red-600 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Failed
              </div>
              <span v-else>{{ upload.status }}</span>
            </td>
            <td class="px-2 py-2 text-sm text-gray-800">
              <button
                  v-if="upload.status === 'PROCESSED'"
                  @click="downloadFile(upload.id)"
                  class="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition flex items-center space-x-2 flex-shrink-0"
              >
                <svg
                    class="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12v6m0 0l-3-3m3 3l3-3"
                  />
                </svg>
                <span>View & Download</span>
              </button>
              <span
                  v-else-if="upload.status === 'FAILED'"
                  class="text-red-600 font-semibold"
              >
                  Error
                </span>
              <span v-else class="text-gray-500">Processing</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Card Layout for small screens -->
      <div class="md:hidden space-y-4">
        <div
            v-for="upload in previousUploads"
            :key="upload.id"
            class="bg-white p-4 rounded-lg shadow"
        >
          <div class="flex justify-between items-center mb-2">
            <span
                class="font-medium text-gray-800 truncate flex-1 mr-2 break-words"
                :title="upload.filename"
            >
              {{ upload.filename }}
            </span>
            <!-- Status Indicator -->
            <div class="text-sm flex-shrink-0">
              <span
                  v-if="upload.status === 'PROCESSING'"
                  class="flex items-center text-blue-600"
              >
                <svg
                    class="animate-spin h-4 w-4 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                  ></circle>
                  <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Processing
              </span>
              <span
                  v-else-if="upload.status === 'PROCESSED'"
                  class="flex items-center text-green-600"
              >
                <svg
                    class="h-4 w-4 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12v6m0 0l-3-3m3 3l3-3"
                  />
                </svg>
                Completed
              </span>
              <span
                  v-else-if="upload.status === 'FAILED'"
                  class="flex items-center text-red-600"
              >
                <svg
                    class="h-4 w-4 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Failed
              </span>
              <span v-else class="text-gray-500">{{ upload.status }}</span>
            </div>
          </div>
          <div class="flex justify-end">
            <button
                v-if="upload.status === 'PROCESSED'"
                @click="downloadFile(upload.id)"
                class="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition flex items-center space-x-2 flex-shrink-0"
            >
              <svg
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
              >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12v6m0 0l-3-3m3 3l3-3"
                />
              </svg>
              <span>View & Download</span>
            </button>
            <span
                v-else-if="upload.status === 'FAILED'"
                class="text-red-600 font-semibold"
            >
              Error
            </span>
            <span v-else class="text-gray-500">Processing</span>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Download Popup Modal -->
  <div v-if="isPopupVisible" class="popup-overlay">
    <div class="popup-content relative">
      <!-- Close Button as Cross Icon -->
      <button
          @click="closePopup"
          class="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          aria-label="Close"
      >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
          <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <!-- Display Original Filename -->
      <h2
          class="text-lg font-semibold mb-4 break-words"
          :title="currentUpload ? currentUpload.filename : ''"
      >
        File Contents: {{ currentUpload ? currentUpload.filename : '' }}
      </h2>

      <!-- Scrollable Text Container -->
      <div class="text-content-container">
        <pre class="text-sm whitespace-pre-wrap">{{ fileContents }}</pre>
      </div>

      <div class="buttons mt-6 flex space-x-4">
        <button
            @click="downloadAsTxt"
            class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 flex items-center space-x-2"
        >
          <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
          >
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 16h8m-8-4h8m-8-4h8m-8-4h8m0 8h8m-8-4h8m-8-4h8"
            />
          </svg>
          <span>Download as .txt</span>
        </button>

        <div class="relative">
          <button
              @click="copyToClipboard"
              class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center space-x-2"
          >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
              <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16h8M8 12h8M8 8h8M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Copy</span>
          </button>

          <!-- Copied Tooltip -->
          <transition name="fade">
            <div
                v-if="copySuccess"
                class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full bg-black text-white text-xs rounded py-1 px-2"
            >
              Copied to clipboard!
            </div>
          </transition>
          <!-- Optional: Copied Error Tooltip -->
          <transition name="fade">
            <div
                v-if="copyError"
                class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full bg-red-600 text-white text-xs rounded py-1 px-2"
            >
              Failed to copy!
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../services/axiosInstance'
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import XHR from '@uppy/xhr-upload'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

const baseURL = process.env.VUE_APP_API_BASE_URL || 'https://localhost/api'

export default {
  name: 'HandwritingToTextPage',
  metaInfo: {
    title: 'Handwriting to Text - EasyMarks AI',
    meta: [
      {
        name: 'description',
        content: 'Convert handwriting to text easily with EasyMarks AI.'
      }
    ]
  },
  data () {
    return {
      sessionUUID: null,
      previousUploads: [],
      refreshInterval: null,
      isPopupVisible: false,
      fileContents: '',
      currentUpload: null, // New data property to store the current upload
      copySuccess: false, // For copy feedback
      copyError: false // For copy error feedback (if implemented)
    }
  },
  mounted () {
    this.sessionUUID = this.getCookie('sessionUUID')
    this.fetchPreviousUploads()
    this.refreshInterval = setInterval(() => {
      this.fetchPreviousUploads()
    }, 10000)

    this.uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: ['image/jpeg', 'image/png', 'image/tiff']
      }
    })
      .use(Dashboard, {
        inline: true,
        target: '#uppy-dashboard',
        width: '100%',
        height: 400,
        hidePoweredByUppy: true,
        locale: {
          strings: {
            upload: 'Convert',
            uploadXFiles: {
              0: 'Convert %{smart_count} file',
              1: 'Convert %{smart_count} files'
            }
          }
        },
        showProgressDetails: true,
        note: 'Supported file types: PNG, JPG, TIFF',
        proudlyDisplayPoweredByUppy: false,
        theme: 'light',
        style: {
          border: '2px solid #007bff',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
        }
      })
      .use(XHR, {
        endpoint: `${baseURL}/handwriting/upload`,
        fieldName: 'file',
        withCredentials: true,
        headers: {
          'X-Session-UUID': this.sessionUUID
        }
      })

    // Listen to Uppy 'upload-success' event
    this.uppy.on('upload-success', (file, response) => {
      // Ensure the backend response contains the necessary data
      if (response && response.body) {
        const newUpload = {
          id: response.body.id, // Adjust based on your backend's response
          filename: response.body.filename || file.name, // Fallback to file.name if not provided
          status: response.body.status || 'PROCESSING' // Default to 'PROCESSING' if not provided
          // Add any other necessary fields from the response
        }
        // Prepend the new upload to show it at the top
        this.previousUploads.unshift(newUpload)
      } else {
        // Fallback: If response does not contain upload details, add with default values
        const newUpload = {
          id: file.id, // Uppy assigns a unique id to each file
          filename: file.name,
          status: 'PROCESSING'
        }
        this.previousUploads.unshift(newUpload)
      }
    })
  },
  beforeUnmount () {
    if (this.refreshInterval) clearInterval(this.refreshInterval)
    if (this.uppy) this.uppy.close()
  },
  methods: {
    async fetchPreviousUploads () {
      try {
        const response = await axios.get(`${baseURL}/handwriting`, {
          headers: { 'X-Session-UUID': this.sessionUUID }
        })
        this.previousUploads = response.data
      } catch (error) {
        console.error('Error fetching previous uploads:', error)
      }
    },
    async downloadFile (uploadId) {
      try {
        const upload = this.previousUploads.find((u) => u.id === uploadId)
        if (!upload) {
          throw new Error('Upload not found')
        }
        const response = await axios.get(`${baseURL}/handwriting/upload/${uploadId}`, {
          headers: { 'X-Session-UUID': this.sessionUUID },
          responseType: 'text'
        })
        this.fileContents = response.data
        this.currentUpload = upload // Set the current upload
        this.isPopupVisible = true
      } catch (error) {
        console.error('Error fetching file contents:', error)
      }
    },
    downloadAsTxt () {
      if (!this.currentUpload) {
        console.error('No upload selected for download')
        return
      }
      // Extract the original filename without extension
      const originalName = this.currentUpload.filename
      const baseName = originalName.replace(/\.[^/.]+$/, '') // Remove extension
      const txtFilename = `${baseName}.txt`

      const blob = new Blob([this.fileContents], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = txtFilename // Use the original filename with .txt extension
      link.click()
      URL.revokeObjectURL(url)
    },
    copyToClipboard () {
      // Check if the Clipboard API is supported
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(this.fileContents)
          .then(() => {
            this.copySuccess = true
            setTimeout(() => {
              this.copySuccess = false
            }, 2000) // Hide the message after 2 seconds
          })
          .catch((err) => {
            console.error('Failed to copy text: ', err)
            this.copyError = true // Optionally handle copy errors
            setTimeout(() => {
              this.copyError = false
            }, 2000)
          })
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = this.fileContents
        // Avoid scrolling to bottom
        textArea.style.top = '0'
        textArea.style.left = '0'
        textArea.style.position = 'fixed'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
          const successful = document.execCommand('copy')
          if (successful) {
            this.copySuccess = true
            setTimeout(() => {
              this.copySuccess = false
            }, 2000)
          }
        } catch (err) {
          console.error('Fallback: Oops, unable to copy', err)
          this.copyError = true // Optionally handle copy errors
          setTimeout(() => {
            this.copyError = false
          }, 2000)
        }

        document.body.removeChild(textArea)
      }
    },
    closePopup () {
      this.isPopupVisible = false
      this.copySuccess = false // Reset copy success when closing
      this.copyError = false // Reset copy error when closing
      this.currentUpload = null // Reset current upload
    },
    getCookie (name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) return parts.pop().split(';').shift()
      return null
    }
  }
}
</script>

<style scoped>
.uploads-table-container {
  margin-top: 20px;
}

.uploads-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.uploads-table th,
.uploads-table td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.uploads-table th {
  background-color: #f4f4f4;
}

.handwriting-font {
  font-family: 'Indie Flower', cursive;
}

.explanation {
  color: darkred;
  margin: 20px 0;
}

/* Popup Overlay Styles */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000; /* Ensures modal appears above all other elements */
}

.popup-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  max-height: 90vh; /* Prevents modal from exceeding viewport height */
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden; /* Ensures child elements don't overflow the modal */
}

/* Adjusted Filename Heading to Prevent Overlap with Close Button */
.popup-content h2 {
  /* Removed properties that prevent wrapping */
  /* overflow: hidden; */
  /* text-overflow: ellipsis; */
  white-space: normal; /* Allows text to wrap onto multiple lines */
  word-break: break-word; /* Breaks long words */
  overflow-wrap: break-word; /* Ensures words break to prevent overflow */
  position: relative;
  padding-right: 40px; /* Space for the close button */
  margin-bottom: 1rem; /* Adjust spacing below the heading */
}

/* Tooltip for Full Filename on Hover */
.popup-content h2:hover::after {
  content: attr(title);
  position: absolute;
  background-color: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  z-index: 2100;
}

/* Close Button Styles */
.popup-content button[aria-label="Close"] {
  position: absolute;
  top: 16px; /* Adjusted to align with padding */
  right: 16px; /* Adjusted to align with padding */
  z-index: 2100; /* Ensures the button is above the heading */
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px; /* Increases clickable area */
}

.popup-content button[aria-label="Close"] svg {
  width: 24px;
  height: 24px;
}

/* Text Content Container */
.text-content-container {
  flex: 1; /* Allows the container to grow and fill available space */
  overflow-y: auto; /* Enables vertical scrolling for long texts */
  margin-top: 1rem;
  padding-right: 0.5rem; /* Prevents content from hiding behind scrollbar */
}

/* Buttons Container */
.buttons {
  display: flex;
  gap: 16px;
}

.buttons button {
  display: flex;
  align-items: center;
}

.buttons button svg {
  margin-right: 8px;
}

/* Relative Positioning for Tooltip */
.relative {
  position: relative;
}

/* Fade Transition for Tooltips */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive Styles */
@media (max-width: 768px) {
  /* Adjust table and card styles for smaller devices */
  .uploads-table th,
  .uploads-table td {
    padding: 8px;
    font-size: 0.875rem; /* Reduce font size for better fit */
  }

  .popup-content {
    padding: 16px;
  }

  .popup-content h2 {
    padding-right: 40px; /* Ensure consistent padding on smaller screens */
    font-size: 1rem; /* Adjust font size if necessary */
  }

  .buttons button {
    padding: 8px 12px;
  }

  .buttons button svg {
    margin-right: 4px;
  }

  .popup-overlay {
    padding: 10px;
  }

  /* Ensure the close button remains accessible on smaller screens */
  .popup-content button[aria-label="Close"] {
    top: 12px;
    right: 12px;
  }

  .popup-content button[aria-label="Close"] svg {
    width: 20px;
    height: 20px;
  }
}

/* Truncate Long Filenames */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Optional: Custom Scrollbar Styling for Text Content */
.text-content-container::-webkit-scrollbar {
  width: 6px;
}

.text-content-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.text-content-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.text-content-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* For Firefox */
.text-content-container {
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
}

/* Additional Styles for Card Layout Adjustments */
.md\:hidden .uploads-table-container .bg-white {
  padding: 1rem;
}

@media (max-width: 768px) {
  /* Ensure the filename and status indicator are properly aligned */
  .md\:hidden .uploads-table-container .flex {
    flex-wrap: nowrap;
  }
}
</style>
